import {
  Assignment,
  Assignments,
} from "classes/models/assignments/assignment.model";
import { AssignmentsService } from "classes/models/assignments/assignments.service";
import { Student } from "classes/models/students/student.model";
import { StudentsService } from "classes/models/students/students.service";
import {
  collection,
  onSnapshot,
  query,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { SubmittedDocument, SubmittedDocuments } from "types/SubmittedDocument";
import { ClassSessions } from "~/classes/models/class-sessions/class-session.model";
import { ClassSessionsService } from "~/classes/models/class-sessions/class-sessions.service";

import { Classroom, Classrooms } from "~/classes/models/classrooms/classroom.model";
import { ClassroomsService } from "~/classes/models/classrooms/classrooms.service";
import { DocumentNotFoundError } from "~/classes/database/document-not-found.error";

import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";

import { catchError, Subscription } from "rxjs";
import { NextAssignmentVisibilities } from "~/types/enums/NextAssignmentVisibilities.enum";

export const useStudentClassroomData = (userId: string, studentId: string) =>
  defineStore(`/users/${userId}/students/${studentId}/data`, () => {
    const student = ref<Student>();
    const classSessions = ref<ClassSessions>([]);

    const classrooms = ref<Classrooms>([]);
    const failedClassroomIds = ref<string[]>([]);
    const assignments = ref<Assignments>([]);
    const submittedDocuments = ref<SubmittedDocuments>([]);

    const classSessionsSubscription = ref<{
      [key: string]: Subscription | undefined;
    }>({});
    const assignmentsSubscription = ref<{
      [key: string]: Subscription | undefined;
    }>({});

    const documentsSubscription = ref<{
      [key: string]: Unsubscribe;
    }>({});

    const isInitialized = ref(false);

    const initializeStudentClassroomData = async () => {
      if (isInitialized.value) return;

      student.value = await StudentsService.get(studentId);

      const classroomIds = student.value.classroomIds;
      classrooms.value = [];
      failedClassroomIds.value = [];

      for (const classroomId of classroomIds) {
        let classroom: Classroom;
        try {
          classroom = await ClassroomsService.get(classroomId);
        } catch (error) {
          if (error instanceof DocumentNotFoundError) {
            console.warn("[student-classrooms] Skipping missing classroom", {
              studentId,
              classroomId,
            });
          } else {
            failedClassroomIds.value.push(classroomId);
            console.error("[student-classrooms] Could not load classroom", {
              studentId,
              classroomId,
              error,
            });
          }
          continue;
        }

        if (classroom.isArchived) continue;

        try {
          classrooms.value = [...classrooms.value, classroom];

          classSessionsSubscription.value[classroomId]?.unsubscribe();

          classSessionsSubscription.value[classroomId] =
            ClassSessionsService.streamClassSessions({
              classroomId,
              userId: useTeacherPesudoStudentLogin().pseudoStudentId
                ? useCurrentUID()
                : undefined,
            })
              .pipe(
                catchError((error) => {
                  return [];
                })
              )
              .subscribe((data) => {
                classSessions.value = data;
              });

          assignmentsSubscription.value[classroomId]?.unsubscribe();
          assignmentsSubscription.value[classroomId] =
            AssignmentsService.streamStudentClassroomAssignments({
              classroomId,
              userId: useTeacherPesudoStudentLogin().pseudoStudentId
                ? useCurrentUID()
                : undefined,
            })
              .pipe(
                catchError((error) => {
                  return [];
                })
              )
              .subscribe((data) => {
                assignments.value = [];
                const newAssignments = data.filter((assignment) => {
                  if (assignment.isArchived == true) return false;

                  return true;
                });

                assignments.value = newAssignments;

                assignments.value = useSortAssignments(assignments.value);

                // Reverser the assignments
                assignments.value = assignments.value.reverse();

                for (const assignment of assignments.value) {
                  if (assignment.id == undefined) continue;

                  useStudentAssignmentDataStore(
                    assignment.id
                  ).initializeStudentAssignmentData();
                }
              });

          const db = useFirestore();
          const submittedDocumentsRef = collection(db, `/documents`);
          const submittedDocumentsQuery = query(
            submittedDocumentsRef,
            where("studentId", "==", studentId),
            where("classroomId", "==", classroomId),
            where("state", "not-in", [DocumentState.archived])
          );

          if (documentsSubscription.value[classroomId]) {
            documentsSubscription.value[classroomId]();
          }

          documentsSubscription.value[classroomId] = onSnapshot(
            submittedDocumentsQuery,
            (snapshot) => {
              submittedDocuments.value = snapshot.docs.map((doc) => {
                return {
                  ...doc.data(),
                  id: doc.id,
                } as SubmittedDocument;
              });
            },
            (error) => {}
          );
        } catch (error) {
          // Empty - This is to prevent issues if the classroom no longer exists for the student
        }
      }

      isInitialized.value = true;
    };

    const assignmentDocuments = computed(() => {
      return (assignmentId: string) => {
        const documents = submittedDocuments.value.filter((doc) => {
          return doc.assignmentId === assignmentId;
        });

        documents.sort((a, b) => {
          // sort by lastUpdateTimestamp
          if (a.lastUpdatedTimestamp < b.lastUpdatedTimestamp) return 1;

          if (a.lastUpdatedTimestamp > b.lastUpdatedTimestamp) return -1;

          return 0;
        });

        return documents;
      };
    });

    const isStudentReviewEnabledForClassroom = computed(() => {
      return (classroomId: string) => {
        const classroom = classrooms.value.find(
          (classroom) => classroom.id === classroomId
        );

        if (!classroom) return false;

        return classroom.enableStudentReview;
      };
    });

    const classroomFromId = computed(() => {
      return (classroomId: string) => {
        return classrooms.value.find(
          (classroom) => classroom.id === classroomId
        );
      };
    });

    const hasClassroomResources = computed(() => {
      return classrooms.value.some((classroom) => {
        return (
          classroom.resources.filter((resource) => {
            return resource.isPublic;
          }).length > 0
        );
      });
    });

    const allowSubmissionType = computed(() => {
      return (
        assignment: Assignment,
        submissionType: DocumentSubmissionType
      ) => {
        switch (submissionType) {
          case DocumentSubmissionType.keyWordOutline:
            if (assignment.type == AssignmentType.general) return false;

            return assignment.allowKWOSubmission != undefined
              ? assignment.allowKWOSubmission
              : classroomFromId.value(assignment.classroomId)
                  ?.allowKWOSubmission;
          case DocumentSubmissionType.roughDraft:
            if (assignment.type == AssignmentType.general) return false;

            return assignment.allowRoughDraftSubmission != undefined
              ? assignment.allowRoughDraftSubmission
              : classroomFromId.value(assignment.classroomId)
                  ?.allowRoughDraftSubmission;
          case DocumentSubmissionType.finalDraft:
            return assignment.allowFinalDraftSubmission;
          default:
            return true;
        }
      };
    });

    const requireSubmissionType = computed(() => {
      return (
        assignment: Assignment,
        submissionType: DocumentSubmissionType
      ) => {
        switch (submissionType) {
          case DocumentSubmissionType.keyWordOutline:
            return assignment.requireKWOSubmission != undefined
              ? assignment.requireKWOSubmission
              : classroomFromId.value(assignment.classroomId)
                  ?.requireKWOSubmission;
          case DocumentSubmissionType.roughDraft:
            return assignment.requireRoughDraftSubmission != undefined
              ? assignment.requireRoughDraftSubmission
              : classroomFromId.value(assignment.classroomId)
                  ?.requireRoughDraftSubmission;
          case DocumentSubmissionType.finalDraft:
          default:
            return true;
        }
      };
    });

    const isAutoReturnEnabledWithAssignmentId = computed(() => {
      return (assignmentId: string) => {
        const assignment = assignments.value.find(
          (assignment) => assignment.id === assignmentId
        );

        if (!assignment) return false;

        return assignment.isAutoReturnEnabled != undefined
          ? assignment.isAutoReturnEnabled
          : classroomFromId.value(assignment.classroomId)?.isAutoReturnEnabled;
      };
    });

    const isAutoReturnEnabled = computed(() => {
      return (assignment: Assignment) => {
        const classroom = classroomFromId.value(assignment.classroomId);

        return assignment.isAutoReturnEnabled != undefined
          ? assignment.isAutoReturnEnabled
          : classroom?.isAutoReturnEnabled;
      };
    });

    const dashboardAssignments = computed(() => {
      const remainingAssignments = [] as Assignments;
      const assignmentsToHide = [] as Assignments;
      const assignmentsToShow = [] as Assignments;

      let lastIncompleteAssignmentIndex = -1;

      for (let i = 0; i < assignments.value.length; i++) {
        const assignment = assignments.value[i];
        // We increment not decrement htis because we show the more recent assignments at the top of the list
        // So the "next" assignment is actually the previous assignment
        const nextAssignment = assignments.value[i + 1];

        const isStudentExcused = nextAssignment?.isStudentExcused(studentId);

        // If hte assingment has an open tiemstamp and it's in the pas then we always show it
        // If the aassignmetn has an open timstmap and it's in the future we never show it.
        if (assignment.openTimestamp != undefined) {
          if (assignment.openTimestamp < Date.now()) {
            assignmentsToShow.push(assignment);
            continue;
          } else {
            assignmentsToHide.push(assignment);
            continue;
          }
        }

        // If the assignment has an open timestamp it means it should be shown to the student
        // If the student is excused from the "next" (aka previous) assignment, then we show this one
        if (
          (assignment.openTimestamp != undefined &&
            assignment.openTimestamp < Date.now()) ||
          nextAssignment == undefined ||
          nextAssignment.id == undefined ||
          isStudentExcused
        ) {
          remainingAssignments.push(assignment);
          continue;
        }

        if (
          nextAssignment.openTimestamp != undefined &&
          nextAssignment.openTimestamp > Date.now()
        ) {
          lastIncompleteAssignmentIndex = i;
          continue;
        }

        // First we get all of the submissions from the "next" assignment, which is actually hte previous assignment in sequence
        // Right now, by default, we require submitted assignments which means the student has completed their portion of hte review
        // - A teacher has the option of configuring it so that all components must be graded before the student can move on.

        const classroom = classroomFromId.value(nextAssignment.classroomId);

        const nextAssignmentSubmissions = assignmentDocuments
          .value(nextAssignment.id)
          .filter((submission) => {
            switch (classroom?.nextAssignmentVisibility) {
              case NextAssignmentVisibilities.graded:
                return submission.state == DocumentState.graded;

              case NextAssignmentVisibilities.accepted:
                return (
                  submission.state == DocumentState.graded &&
                  submission.documentGradedState == DocumentGradedState.accepted
                );

              // In the future I could allow a teacher to configure this option if they want.
              // I may also want to assume that graded and returned mena the same thign in this context
              // case "returned":
              //   return submission.isReturnedToStudent;
              case "submitted":
              default:
                return (
                  submission.state != DocumentState.studentReview &&
                  submission.state != DocumentState.editing
                );
            }
          });

        const nextAssignmentTypes = [
          ...new Set(
            nextAssignmentSubmissions.map(
              (submission) => submission.documentSubmissionType
            )
          ),
        ];

        const allowKWOSubmission = allowSubmissionType.value(
          nextAssignment,
          DocumentSubmissionType.keyWordOutline
        );

        const requireKWOSubmission = requireSubmissionType.value(
          nextAssignment,
          DocumentSubmissionType.keyWordOutline
        );

        const allowRoughDraftSubmission = allowSubmissionType.value(
          nextAssignment,
          DocumentSubmissionType.roughDraft
        );

        const requireRoughDraftSubmission = requireSubmissionType.value(
          nextAssignment,
          DocumentSubmissionType.roughDraft
        );

        const allowFinalDraftSubmission = allowSubmissionType.value(
          nextAssignment,
          DocumentSubmissionType.finalDraft
        );

        if (
          allowKWOSubmission &&
          requireKWOSubmission &&
          nextAssignmentTypes.includes(DocumentSubmissionType.keyWordOutline) !=
            true
        ) {
          lastIncompleteAssignmentIndex = i;
          continue;
        }

        if (
          allowRoughDraftSubmission &&
          requireRoughDraftSubmission &&
          nextAssignmentTypes.includes(DocumentSubmissionType.roughDraft) !=
            true
        ) {
          lastIncompleteAssignmentIndex = i;
          continue;
        }

        if (
          nextAssignmentTypes.includes(DocumentSubmissionType.finalDraft) !=
          true
        ) {
          lastIncompleteAssignmentIndex = i;
          continue;
        }

        remainingAssignments.push(assignment);
      }

      var assignmentsCopy = [...assignments.value];

      if (lastIncompleteAssignmentIndex != -1) {
        assignmentsCopy = assignmentsCopy.splice(
          lastIncompleteAssignmentIndex + 1,
          assignmentsCopy.length
        );
      }

      const combinedAssignments = [...assignmentsToShow, ...assignmentsCopy];

      // Remove duplicates
      var filteredAssignments = combinedAssignments.filter(
        (assignment, index, self) =>
          index === self.findIndex((t) => t.id === assignment.id)
      );

      filteredAssignments.sort((a, b) => {
        return (b.displayOrder ?? 0) - (a.displayOrder ?? 0);
      });

      const assignmentsToHideIds = assignmentsToHide.map((assignment) => {
        return assignment.id;
      });

      return filteredAssignments.filter((assignment) => {
        return assignmentsToHideIds.includes(assignment.id) != true;
      });
    });

    return {
      initializeStudentClassroomData,
      classroomFromId,
      classrooms,
      failedClassroomIds,
      student,
      classSessions,
      assignments,
      submittedDocuments,
      assignmentDocuments,
      isStudentReviewEnabledForClassroom,
      hasClassroomResources,
      dashboardAssignments,
      allowSubmissionType,
      requireSubmissionType,
      isAutoReturnEnabled,
      isAutoReturnEnabledWithAssignmentId,
    };
  });
